import { Box, Button, Checkbox, Chip, Divider, FormControlLabel, Grid, IconButton, Paper, Theme, Tooltip } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridOverlay,
  GridRenderCellParams,
  GridValueGetterParams,
  GridValueSetterParams,
} from "@mui/x-data-grid";
import makeStyles from "@mui/styles/makeStyles";
import EditIcon from '@mui/icons-material/Edit';
import { ChangeEvent, useEffect, useState } from "react";
import { FC } from "react";
import { useSelector } from "react-redux";
import { IRootReducer } from "../../../reducers";
import { renderCellExpand } from "../../common/data-grid/cell-expander";
import DataGridUserInfoCell from "../../common/data-grid/user-info";
import { IncidentAction } from "../../../reducers/states/vcc-incident";
import { ActionStatus } from "../../../enums/action-status";
import { ActionStatusTextColor } from "../action-status-text-color";
import TimeDisplay from "../../common/time-display";
import AddActionModal from "../add-action-modal";
import useUserPermissions from "../../../hooks/user-permissions";
import { RoleAction } from "../../../enums/permission-actions";
import { useAgency } from "../../../hooks/agencies";

const useStyles = makeStyles((theme: Theme) => ({
  removedAction: {
    color: theme.marker.ClosedDispatchEventIFD,
  },
  chipRemoved: {
    backgroundColor: theme.marker.ClosedDispatchEventIFD,
    color: "white",
  },
}));

export const defaultStatusFilter = [ActionStatus.IN_PROGRESS, ActionStatus.PLANNED, ActionStatus.COMPLETE, ActionStatus.REMOVED];
export const openOnlyStatusFilter = [ActionStatus.IN_PROGRESS, ActionStatus.PLANNED];
interface ActionGridRow {
  id: string;
  description: string;
  type: string;
  agency: string;
  createdBy: string;
  lead?: string;
  status?: string;
  timeAdded: string | Date;
  fromTimePlanned?: string | Date;
  toTimePlanned?: string | Date;
  collaborators: string[];
  mentions: string;
}

interface ActionGridProps {
  props: {
    handleModify: (row: IncidentAction) => void;
    isEditEnabled: boolean;
  }
}

const ActionGrid: FC<ActionGridProps> = ({ props }) => {
  const classes = useStyles();
  const [roleDenied] = useUserPermissions(RoleAction.ADD_AND_EDIT_MODBILITY_STRATEGIES);
  const addEditEnabled = !roleDenied && props.isEditEnabled;
  const [gridData, setGridData] = useState([] as ActionGridRow[]);
  const [addActionOpen, setAddActionOpen] = useState(false);
  const [statusFilters, setStatusFilters] = useState(defaultStatusFilter);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0
  })

  const incidentStore = useSelector(
    (state: IRootReducer) => state.vccIncidentReducer.incident
  );

  const actionStore = useSelector(
    (state: IRootReducer) => state.vccIncidentReducer.actions
  );

  useEffect(() => {
    if (actionStore.length > 0) {
      const gridRows = actionStore.reduce((results: ActionGridRow[], r) => {
        if (statusFilters.includes(r.status as ActionStatus)) {
          results.push({
            id: r.id,
            description: r.description,
            type: r.type,
            createdBy: r.createdBy,
            lead: r.lead,
            agency: r.agency,
            status: r.status,
            timeAdded: r.timeAdded,
            fromTimePlanned: r.fromTimePlanned,
            toTimePlanned: r.toTimePlanned,
            collaborators: r.collaborators,
            mentions: r.mentions,
          });
        }
        return results;
      }, []);

      setGridData(gridRows);
    }
  }, [actionStore, statusFilters]);

  function setArrayGridCell(field: string, params: GridValueSetterParams) {
    var value = params.value!.toString();
    return { ...params.row, [field]: value.split(",") };
  }

  const handleModifyAction = (actionId: string) => {
    var incidentAction: IncidentAction = actionStore.find(a => a.id === actionId)!;
    props.handleModify(incidentAction)
  }

  const handleStatusSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.checked
      ? setStatusFilters(openOnlyStatusFilter)
      : setStatusFilters(defaultStatusFilter);
  };

  const AgencyCell: FC<{ id: string }> = ({ id }) => {
    const agency = useAgency(id);
    return <Tooltip title={agency?.name} arrow><span>{agency?.abbreviation}</span></Tooltip>
  }

  const columns: GridColDef[] = [
    {
      field: "editAction",
      headerName: "Edit",
      filterable: false,
      flex: .25,
      renderCell: (params: GridRenderCellParams<ActionGridRow>) => {
        return (
          <IconButton onClick={() => handleModifyAction(params.row.id)}>
            {params.row.status === ActionStatus.REMOVED ? <EditIcon style={{ color: '#C0C0C0' }} /> : <EditIcon style={{ color: 'black' }} />}
          </IconButton>
        )
      }
    },

    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      renderCell: renderCellExpand,
    },
    {
      field: "timePlanned",
      headerName: "Time Planned",
      flex: 1,
      valueGetter: (params: GridRenderCellParams<ActionGridRow>) =>
        params.row.fromTimePlanned ? params.row.fromTimePlanned + " - " + params.row.toTimePlanned : "",
    },
    {
      field: "createdBy",
      headerName: "Added By",
      flex: 1,
      renderCell: (params: GridRenderCellParams<ActionGridRow>) =>
        renderCellExpand(
          params,
          <DataGridUserInfoCell users={[params.row.createdBy]} />,
          true
        ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params: GridRenderCellParams<ActionGridRow>) => {
        return <ActionStatusTextColor value={params.row.status ?? ''} />
      }
    },
    {
      field: "agency",
      headerName: "Agency",
      flex: 0.65,
      renderCell: (params: GridRenderCellParams<ActionGridRow>) => <AgencyCell id={params.row.agency} />
    },
    {
      field: "collaborators",
      headerName: "Collaborators",
      flex: 1,
      renderCell: (params: GridRenderCellParams<ActionGridRow>) =>
        renderCellExpand(
          params,
          <DataGridUserInfoCell users={params.row.collaborators} />,
          true
        ),
      valueGetter: (params: GridValueGetterParams) =>
        params.row.collaborators.join(", "),
      valueSetter: (params: GridValueSetterParams) =>
        setArrayGridCell("collaborators", params),
    },
  ];

  return (
    <>
      <Box m={1} display="flex" style={{ height: "100%" }}>
        <Grid container style={{ height: "100%" }}>
          <Grid item xs={12}>
            <Paper variant="outlined">
              <Grid container item xs={12}>
                <Grid container item xs={6} alignItems="center">
                  <Grid item xs={12} m={1}>
                    <TimeDisplay />
                  </Grid>
                </Grid>
                <Grid container item xs={6} justifyContent="flex-end">
                  <Grid item>
                    <Tooltip title="Displays only active mobility strategies when toggled on">
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="Open Only"
                            onChange={handleStatusSwitchChange}
                            checkedIcon={<Chip color={"primary"} variant="filled" label="Open Only" />}
                            icon={
                              <Chip
                                variant="outlined"
                                label="Open Only"
                              />
                            }
                          />
                        }
                        label=""
                      />
                    </Tooltip>
                  </Grid>
                  {addEditEnabled && (
                    <Grid item m={1} mr={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ alignSelf: "center" }}
                        size="small"
                        onClick={() => setAddActionOpen(true)}
                      >
                        Add Strategy
                      </Button>
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} style={{ height: "85%" }}>
            <DataGrid
              density="compact"
              rows={gridData}
              columns={columns}
              getRowClassName={(params) =>
                params.row.status === "REMOVED" ? classes.removedAction : ""
              }
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              disableRowSelectionOnClick
              columnVisibilityModel={{
                editAction: addEditEnabled,
              }}
              components={{
                NoRowsOverlay: () => (
                  <GridOverlay>
                    No actions added - select "Add Strategy" to begin coordination of
                    Congestion Management Plan.
                  </GridOverlay>
                ),
                NoResultsOverlay: () => (
                  <GridOverlay>No filtered strategy results</GridOverlay>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <AddActionModal
        props={{
          isOpen: addActionOpen,
          incidentId: incidentStore.id,
          handleClose: () => setAddActionOpen(false),
        }}
      />
    </>
  );
};

export default ActionGrid;
